import { api } from './api';

export interface BookingTimelineEntry {
  action: string;
  substatus?: 'pending payment' | 'pending approval from school';
  status: 'pending' | 'approved' | 'cancelled' | 'rejected';
  performedBy: 'USER' | 'SCHOOL' | 'SYSTEM';
  performedAt: string;
  notes?: string;
}

export interface Booking {
  _id: string;
  bookingReference?: string; // Human-readable ID with BY- prefix (e.g. BY-A1B2C3D4)
  userId?: string;
  schoolId: string;
  courseId?: string;
  status: 'pending' | 'approved' | 'cancelled' | 'rejected';
  substatus?: 'pending payment' | 'pending approval from school';
  name: string;
  email: string;
  phone?: string;
  message?: string;
  selectedMonth?: string;
  accommodationType?: string;
  bookingAmount?: number;
  bookingCurrency?: string;
  paymentLink?: string;
  actionTimeline?: BookingTimelineEntry[];
  notes?: string;
  submittedAt: string;
  approvedAt?: string;
  rejectedAt?: string;
  paymentCompletedAt?: string;
  createdAt: string;
  updatedAt: string;
  schoolName?: string;
  courseTitle?: string;
  schoolId_populated?: {
    name: string;
    location?: string;
    city?: string;
    country?: string;
  };
  courseId_populated?: {
    name: string;
    duration?: number;
    type?: string;
    price?: number;
    currency?: string;
    difficulty?: string;
    accommodation?: {
      available: boolean;
      rooms?: Array<{
        roomId?: string;
        type: string;
        capacity: number;
        price: number;
        bookingAmount?: number;
        amenities: string[];
        description?: string;
      }>;
      mealPlan?: {
        included: boolean;
        type: string;
        description?: string;
      };
    };
    schedule?: {
      startDate?: string;
      endDate?: string;
      activeMonths?: number[];
    };
    inclusions?: string[];
    requirements?: string[];
  };
}

export interface BookingsResponse {
  success: boolean;
  data: {
    bookings: Booking[];
    totalCount: number;
    currentPage: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
}

export interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

export interface CreateBookingData {
  schoolId: string;
  courseId?: string;
  selectedMonth: string;
  accommodationType?: string;
  name: string;
  email: string;
  phone?: string;
  message?: string;
}

export const bookingService = {
  async getMyBookings(page: number = 1, limit: number = 10, status?: string): Promise<BookingsResponse> {
    const requestBody: any = {
      page,
      limit,
    };
    
    if (status && status !== 'ALL') {
      requestBody.status = status;
    }

    const response = await api.post<BookingsResponse>('/v1/bookings/my', requestBody);
    return response.data;
  },

  async getBooking(bookingId: string): Promise<ApiResponse<Booking>> {
    const response = await api.get<ApiResponse<Booking>>(`/v1/bookings/${bookingId}`);
    return response.data;
  },

  async createBooking(data: CreateBookingData): Promise<ApiResponse<Booking>> {
    const response = await api.post<ApiResponse<Booking>>('/bookings', data);
    return response.data;
  },

  async approveBooking(bookingId: string, notes?: string): Promise<ApiResponse<Booking>> {
    const response = await api.post<ApiResponse<Booking>>(`/bookings/${bookingId}/approve`, { notes });
    return response.data;
  },

  async rejectBooking(bookingId: string, notes?: string): Promise<ApiResponse<Booking>> {
    const response = await api.post<ApiResponse<Booking>>(`/bookings/${bookingId}/reject`, { notes });
    return response.data;
  },

  async completePayment(bookingId: string): Promise<ApiResponse<Booking>> {
    const response = await api.post<ApiResponse<Booking>>(`/v1/bookings/${bookingId}/complete-payment`);
    return response.data;
  },
};

